/**
 * 10.9GR — PASSE DE ACABAMENTO (pós-processamento): vinheta + grão + correção de cor por região.
 *
 * Um único ShaderPass barato (1 leitura de textura + aritmética) depois do bloom e ANTES do
 * OutputPass (tone mapping + sRGB), então a correção trabalha em linear. Por região:
 *   · `tint`: cor multiplicada de leve (Floresta quente-verde, Vazio frio-azul…);
 *   · `saturation`: 1 = neutro;
 *   · `contrast`: 1 = neutro;
 *   · `lift`: levanta as sombras (0 = preto puro);
 * Vinheta e grão são globais (grão animado, bem sutil — esconde banding no céu/névoa).
 */
import * as THREE from "three";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

export interface GradeParams {
  tint: number; // hex
  saturation: number;
  contrast: number;
  lift: number;
  vignette: number; // 0..1 força
}

export const DEFAULT_GRADE: GradeParams = { tint: 0xffffff, saturation: 1.06, contrast: 1.04, lift: 0.0, vignette: 0.32 };
/** Dentro do Void (Fenda dos Titãs, arena da raid): violeta frio, mais contraste, vinheta fechada. */
export const VOID_GRADE: GradeParams = { tint: 0xe9dcff, saturation: 1.0, contrast: 1.12, lift: 0.0, vignette: 0.48 };

const GradeShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTint: { value: new THREE.Color(1, 1, 1) },
    uSat: { value: 1.06 },
    uCon: { value: 1.04 },
    uLift: { value: 0.0 },
    uVig: { value: 0.32 },
    uGrain: { value: 0.035 },
    uTime: { value: 0 },
    uFlash: { value: 0 }, // 10.9GR — clarão branco (dano forte no player / relâmpago), decai sozinho
    uAspect: { value: 1 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec3 uTint;
    uniform float uSat, uCon, uLift, uVig, uGrain, uTime, uFlash, uAspect;
    varying vec2 vUv;
    float hash12(vec2 p) {
      vec3 p3 = fract(vec3(p.xyx) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }
    void main() {
      vec4 c = texture2D( tDiffuse, vUv );
      vec3 col = c.rgb * uTint;
      // saturação (luma Rec.709 em linear)
      float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
      col = mix(vec3(l), col, uSat);
      // contraste em torno do cinza médio (linear ~0.18)
      col = (col - 0.18) * uCon + 0.18;
      // lift: levanta as sombras sem estourar as luzes
      col = col + uLift * (1.0 - col);
      // vinheta suave, respeitando o aspecto (elíptica na horizontal)
      vec2 d = (vUv - 0.5) * vec2(uAspect, 1.0) / vec2(max(uAspect, 1.0), 1.0);
      float r = dot(d, d) * 2.4;
      col *= 1.0 - uVig * smoothstep(0.35, 1.35, r);
      // grão animado (esconde banding), só nas médias/escuras
      float g = hash12(vUv * vec2(1920.0, 1080.0) + fract(uTime * 7.3) * 100.0) - 0.5;
      col += g * uGrain * (1.0 - l);
      // clarão
      col = mix(col, vec3(1.0), uFlash);
      gl_FragColor = vec4(max(col, 0.0), c.a);
    }`,
};

export class GradePass extends ShaderPass {
  private flashV = 0;
  constructor() {
    super(GradeShader);
  }
  apply(p: GradeParams): void {
    (this.uniforms.uTint.value as THREE.Color).setHex(p.tint);
    this.uniforms.uSat.value = p.saturation;
    this.uniforms.uCon.value = p.contrast;
    this.uniforms.uLift.value = p.lift;
    this.uniforms.uVig.value = p.vignette;
  }
  /** Clarão branco instantâneo (0..1), decai em ~0,25 s. */
  flash(strength: number): void {
    this.flashV = Math.min(0.6, Math.max(this.flashV, strength));
  }
  tick(dt: number, aspect: number): void {
    this.uniforms.uTime.value += dt;
    this.uniforms.uAspect.value = aspect;
    if (this.flashV > 0) {
      this.flashV = Math.max(0, this.flashV - dt * 2.6);
      this.uniforms.uFlash.value = this.flashV;
    }
  }
}
